import { CheckBox, ChevronLeft, CircleOutlined } from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Avatar,
  Box,
  Button,
  FormControl,
  IconButton,
  Menu,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import { styled } from "@mui/system";
import {
  DotsThreeVerticalIcon,
  PencilIcon,
  TrashIcon
} from "@phosphor-icons/react";
import { LoadingScreen } from "components/LoadingScreen";
import { DeleteModal, EthiopianDatePicker } from "components/UI";
import { MutationError } from "components/UI/MutationError";
import { GOOGLE_API_KEY, IMAGE_URL } from "config";
import {
  ADDRESSES,
  usePublicCities,
  usePublicCountries,
  usePublicRegions,
  usePublicSubcities,
  usePublicWoredas,
  usePublicZones
} from "features/members";
import { merge } from "lodash";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { usePlacesWidget } from "react-google-autocomplete";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useCentralStore } from "stores";
import {
  useCreateDenominationReport,
  useDeleteDenominationReport,
  useDenomination,
  useDenominationChurchesPublic,
  useDenominationReport,
  useUpdateDenominationReport
} from "../api";
import { Section0 } from "../components/reporting-form/sections/Section0";
import { Section1 } from "../components/reporting-form/sections/Section1";
import { Section10 } from "../components/reporting-form/sections/Section10";
import { Section11 } from "../components/reporting-form/sections/Section11";
import { Section12 } from "../components/reporting-form/sections/Section12";
import { Section2 } from "../components/reporting-form/sections/Section2";
import { Section2New } from "../components/reporting-form/sections/Section2New";
import { Section3 } from "../components/reporting-form/sections/Section3";
import { Section4 } from "../components/reporting-form/sections/Section4";
import { Section5 } from "../components/reporting-form/sections/Section5";
import { Section6 } from "../components/reporting-form/sections/Section6";
import { Section7 } from "../components/reporting-form/sections/Section7";
import { Section8 } from "../components/reporting-form/sections/Section8";
import { Section9 } from "../components/reporting-form/sections/Section9";
import { SuccessModal } from "../components/reporting-form/SuccessModal";

const StyledHeader = styled(Box)(({ theme }) => ({
  background: `linear-gradient(to right, #00B0B9, #008087, #00B0B9)`,
  color: "white",
  padding: "20px",
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "center",
  gap: "24px",
  position: "relative",
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
    padding: "12px 8px",
    gap: "12px"
  }
}));

const StyledForm = styled("form")({
  padding: "20px",
  "& .MuiTextField-root, & .MuiFormControl-root": {
    marginBottom: "20px"
  },
  width: "100%",
  maxWidth: "1200px",
  margin: "0 auto"
});

const StyledAccordionTitle = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "10px",
  flex: 1,
  fontSize: "0.75rem",
  "& .MuiSvgIcon-root": {
    color: "#4CAF50"
  }
});

const StyledAccordion = styled(Accordion)(({ theme, expanded }) => ({
  border: expanded
    ? `1px solid ${theme.palette.primary.main}`
    : "1px solid #e0e0e0",
  borderRadius: "4px !important",
  marginBottom: "8px",
  "&:before": {
    display: "none"
  },
  "& .MuiAccordionSummary-root": {
    borderRadius: "4px"
  },
  "& .MuiAccordionSummary-expandIconWrapper": {
    transform: "none !important"
  }
}));

const StyledSelect = styled(Select)({
  borderColor: "#E9EAEC",
  borderRadius: "10px",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#E9EAEC",
    borderRadius: "10px"
  }
});

const StyledTextField = styled(TextField)({
  minWidth: "80px",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#E9EAEC",
    borderRadius: "10px"
  },
  "& .MuiInputBase-root.Mui-disabled": {
    backgroundColor: "rgb(255, 255, 255)",
    borderColor: "rgb(179, 179, 179)",
    outline: "none",
    "& fieldset": {
      borderColor: "rgb(179, 179, 179)",
      borderWidth: "0.5px"
    }
  }
});

const TODAY = moment().format("YYYY-MM-DD");

const isValidEthiopianPhoneNumber = phone => {
  if (!phone) return true; // Allow empty value
  const ethiopianPhoneRegex = /^(\+251|0)\d{9}$/;
  return ethiopianPhoneRegex.test(phone);
};

export const DenominationReporting = () => {
  const [searchParams] = useSearchParams();
  const reportId = searchParams.get("reportId");

  const rawStartDate = searchParams.get("start");
  const rawEndDate = searchParams.get("end");
  const rawTitle = searchParams.get("title");

  const { t } = useTranslation();

  // Check for missing parameters
  if (!reportId && (!rawStartDate || !rawEndDate || !rawTitle)) {
    return <BrokenLinkScreen reason={t("common.missingRequiredParameters")} />;
  }

  // Validate and format dates
  const reportStartDate = moment(rawStartDate);
  const reportEndDate = moment(rawEndDate);

  if (!reportId && (!reportStartDate.isValid() || !reportEndDate.isValid())) {
    return <BrokenLinkScreen reason={t("common.invalidDateFormat")} />;
  }

  if (!reportId && reportStartDate.isAfter(reportEndDate)) {
    return (
      <BrokenLinkScreen reason={t("common.reportStartDateAfterEndDate")} />
    );
  }

  // Use formatted dates
  const formattedStartDate = reportStartDate.format("YYYY-MM-DD");
  const formattedEndDate = reportEndDate.format("YYYY-MM-DD");

  const [formState, setFormState] = useState({
    // Church Info
    churchInfo: {
      denomination_church_id: "",
      church_founded_date: TODAY,
      report_start_date: formattedStartDate,
      report_end_date: formattedEndDate,
      title: decodeURIComponent(rawTitle),
      country_id: "66",
      region_id: "",
      zone_id: "",
      city_id: "",
      sub_city_id: "",
      woreda_id: "",
      kebele: "",
      house_no: "",
      specific_location: "",
      contact_person_name: "",
      contact_person_phone: "",
      contact_person_email: "",
      latitude: 0,
      longitude: 0
    },
    // Section 0: Member counts
    memberCounts: {
      total: 0,
      baptized: { male: 0, female: 0, total: 0 },
      unbaptized: { male: 0, female: 0, total: 0 },
      children: { male: 0, female: 0, total: 0 },
      baptized_in_branch: { male: 0, female: 0, total: 0 },
      unbaptized_in_branch: { male: 0, female: 0, total: 0 },
      children_in_branch: { male: 0, female: 0, total: 0 },
      total_in_branches: 0
    },
    // Section 1: Resigned members
    resignedMembers: {
      with_letter: { male: 0, female: 0, total: 0 },
      returned_to_world: { male: 0, female: 0, total: 0 },
      deceased: { male: 0, female: 0, total: 0 },
      unknown_reason: { male: 0, female: 0, total: 0 },
      total_resigned: { male: 0, female: 0, total: 0 },
      total_in_branches: 0
    },
    // Section 2: New members
    newMembers: {
      accepted: { male: 0, female: 0, total: 0 },
      baptized: { male: 0, female: 0, total: 0 },
      in_salvation_class: { male: 0, female: 0, total: 0 },
      growth_percentage: 0
    },
    // Section 3 New: Migrated believers
    migratedBelievers: {
      new_members: { male: 0, female: 0, total: 0 }
    },
    // Section 3: Servants education
    servantsEducation: {
      not_appointed: {
        below_diploma: { male: 0, female: 0, total: 0 },
        diploma: { male: 0, female: 0, total: 0 },
        degree: { male: 0, female: 0, total: 0 },
        masters: { male: 0, female: 0, total: 0 },
        phd: { male: 0, female: 0, total: 0 }
      },
      missionaries: {
        below_diploma: { male: 0, female: 0, total: 0 },
        diploma: { male: 0, female: 0, total: 0 },
        degree: { male: 0, female: 0, total: 0 },
        masters: { male: 0, female: 0, total: 0 },
        phd: { male: 0, female: 0, total: 0 }
      },
      teachers: {
        below_diploma: { male: 0, female: 0, total: 0 },
        diploma: { male: 0, female: 0, total: 0 },
        degree: { male: 0, female: 0, total: 0 },
        masters: { male: 0, female: 0, total: 0 },
        phd: { male: 0, female: 0, total: 0 }
      },
      evangelists: {
        below_diploma: { male: 0, female: 0, total: 0 },
        diploma: { male: 0, female: 0, total: 0 },
        degree: { male: 0, female: 0, total: 0 },
        masters: { male: 0, female: 0, total: 0 },
        phd: { male: 0, female: 0, total: 0 }
      },
      pastors: {
        below_diploma: { male: 0, female: 0, total: 0 },
        diploma: { male: 0, female: 0, total: 0 },
        degree: { male: 0, female: 0, total: 0 },
        masters: { male: 0, female: 0, total: 0 },
        phd: { male: 0, female: 0, total: 0 }
      }
    },
    // Section 4: Church growth
    churchGrowth: {
      new_branches: 0,
      branches_to_church: 0,
      total_branches: 0,
      branch_names: ""
    },
    // Section 5: Pastoral training
    pastoralTraining: {
      pastors: { male: 0, female: 0, total: 0 },
      evangelists: { male: 0, female: 0, total: 0 },
      teachers: { male: 0, female: 0, total: 0 },
      missionaries: { male: 0, female: 0, total: 0 },
      unordainedServants: { male: 0, female: 0, total: 0 }
    },
    // Section 6: Gospel spread
    gospelSpread: {
      heard_gospel: { male: 0, female: 0, total: 0 }
    },
    // Section 7: Home fellowship
    homeFellowship: {
      total: 0,
      in_fellowship: { male: 0, female: 0, total: 0 },
      not_in_fellowship: { male: 0, female: 0, total: 0 }
    },
    // Section 8: Training
    training: {
      evangelism: { male: 0, female: 0, total: 0 },
      choir: { male: 0, female: 0, total: 0 },
      key_teachers: { male: 0, female: 0, total: 0 },
      small_group_leaders: { male: 0, female: 0, total: 0 },
      shepherding: { male: 0, female: 0, total: 0 },
      sunday_school_teachers: { male: 0, female: 0, total: 0 },
      married_couples: { male: 0, female: 0, total: 0 },
      unmarried_youth: { male: 0, female: 0, total: 0 },
      youth: { male: 0, female: 0, total: 0 },
      sisters: { male: 0, female: 0, total: 0 },
      general_servants: { male: 0, female: 0, total: 0 },
      growing_youth: { male: 0, female: 0, total: 0 },
      former_leaders: { male: 0, female: 0, total: 0 },
      department_leaders: { male: 0, female: 0, total: 0 },
      new_leaders: { male: 0, female: 0, total: 0 },
      peace_building: { male: 0, female: 0, total: 0 },
      dual_ministry: { male: 0, female: 0, total: 0 },
      others: ""
    },
    // Section 9: Contributions
    contributions: {
      expected_regional: 0,
      paid_regional: 0,
      paid_regional_others: 0,
      expected_main: 0,
      paid_main: 0,
      paid_main_others: 0
    },
    // Section 10: Employees
    employees: {
      permanent: { male: 0, female: 0, total: 0 },
      temporary: { male: 0, female: 0, total: 0 }
    },
    // Section 11: Income
    income: {
      welfare: { number: 0, in_words: "" },
      tithe: { number: 0, in_words: "" },
      offering: { number: 0, in_words: "" },
      gift: { number: 0, in_words: "" },
      other: { number: 0, in_words: "" },
      total: { number: 0, in_words: "" }
    },
    // Section 12: Properties
    properties: {
      government_granted_places: { number: 0, area: 0 },
      purchased_places: { number: 0, area: 0 },
      gifted_places: { number: 0, area: 0 },
      leased_places: { number: 0, area: 0 },
      rented_places: { number: 0, area: 0 },
      church_owned_places: { number: 0, area: 0 },
      worship_halls: { number: 0, area: 0 },
      multi_purpose_buildings: { number: 0, area: 0 },
      vehicles: 0,
      motorcycles_and_three_wheelers: 0,
      fixed_and_others: 0,
      kindergartens: 0,
      primary_schools: 0,
      secondary_schools: 0,
      bible_schools: 0,
      bible_colleges: 0,
      cemeteries: 0
    },
    // Section 13: Thanksgiving
    thanksgiving: {
      main_points: "",
      additional_points: "",
      problems: ""
    }
  });

  const [completedSections, setCompletedSections] = useState({});
  const [expandedSection, setExpandedSection] = useState(0);
  const [openSuccessModal, setOpenSuccessModal] = useState(false);
  const [enableEdit, setEnableEdit] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const open = Boolean(anchorEl);

  const handleClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const { denominationId, formToken } = useParams();
  const openDeleteModal = useCentralStore(state => state.openDeleteModal);

  const denominationReportCreateMutation = useCreateDenominationReport();
  const denominationReportUpdateMutation = useUpdateDenominationReport();
  const denominationReportDeleteMutation = useDeleteDenominationReport();

  const denominationReportMutation = reportId
    ? denominationReportUpdateMutation
    : denominationReportCreateMutation;

  const navigate = useNavigate();

  const denominationChurchesPublicQuery = useDenominationChurchesPublic({
    denominationId,
    config: {
      enabled: !!denominationId
    }
  });

  const {
    data: denomination,
    isLoading: isDenominationLoading,
    isError: isDenominationError
  } = useDenomination({
    denominationId,
    isPublic: true,
    config: {
      enabled: !!denominationId
    }
  });

  const churches = useMemo(() => {
    return denominationChurchesPublicQuery.data?.result || [];
  }, [denominationChurchesPublicQuery.data]);

  const { data: reportData, isLoading: isReportsLoading } =
    useDenominationReport({
      denominationId,
      reportId,
      config: {
        enabled: !!denominationId && !!reportId
      }
    });

  const { ref: placesRef } = usePlacesWidget({
    apiKey: GOOGLE_API_KEY,
    onPlaceSelected: place => {
      handleChange("churchInfo", "specific_location", place.formatted_address);
    },
    options: {
      types: ["establishment"],
      componentRestrictions: { country: "et" }
    }
  });

  const countriesQuery = usePublicCountries();

  const countires = useMemo(() => {
    return countriesQuery.data?.result || [];
  }, [countriesQuery.data]);

  useEffect(() => {
    if (countires.length > 0 && reportData) {
      const report = reportData?.result;

      if (report) {
        handleChange(
          "churchInfo",
          "country_id",
          String(report.country_id || "")
        );
      }
    }
  }, [countires, reportData]);

  const regionsQuery = usePublicRegions({
    countryId: formState.churchInfo.country_id,
    config: {
      enabled: !!formState.churchInfo.country_id
    }
  });

  const zonesQuery = usePublicZones({
    regionId: formState.churchInfo.region_id,
    config: {
      enabled: !!formState.churchInfo.region_id
    }
  });

  const citiesQuery = usePublicCities({
    zoneId: formState.churchInfo.zone_id,
    config: {
      enabled: !!formState.churchInfo.zone_id
    }
  });

  const subcitiesQuery = usePublicSubcities({
    cityId: formState.churchInfo.city_id,
    config: {
      enabled: !!formState.churchInfo.city_id
    }
  });

  const woredasQuery = usePublicWoredas({
    subcityId: formState.churchInfo.sub_city_id,
    config: {
      enabled: !!formState.churchInfo.sub_city_id
    }
  });

  useEffect(() => {
    if (regionsQuery.data && reportData) {
      const report = reportData?.result;

      if (report) {
        handleChange("churchInfo", "region_id", String(report.region_id || ""));
      }
    }
  }, [regionsQuery.data, reportData]);

  useEffect(() => {
    if (zonesQuery.data && reportData) {
      const report = reportData?.result;

      if (report) {
        handleChange("churchInfo", "zone_id", String(report.zone_id || ""));
      }
    }
  }, [zonesQuery.data, reportData]);

  useEffect(() => {
    if (citiesQuery.data && reportData) {
      const report = reportData?.result;

      if (report) {
        handleChange("churchInfo", "city_id", String(report.city_id || ""));
      }
    }
  }, [citiesQuery.data, reportData]);

  useEffect(() => {
    if (subcitiesQuery.data && reportData) {
      const report = reportData?.result;

      if (report) {
        handleChange(
          "churchInfo",
          "sub_city_id",
          String(report.sub_city_id || "")
        );
      }
    }
  }, [subcitiesQuery.data, reportData]);

  useEffect(() => {
    if (woredasQuery.data && reportData) {
      const report = reportData?.result;

      if (report) {
        handleChange("churchInfo", "woreda_id", String(report.woreda_id || ""));
      }
    }
  }, [woredasQuery.data, reportData]);

  useEffect(() => {
    if (reportData) {
      const report = reportData?.result;

      if (report) {
        // Pre-fill form with existing report data
        setFormState(prev => ({
          ...prev,
          churchInfo: {
            denomination_church_id:
              report.denomination_church_id ||
              prev.churchInfo.denomination_church_id,
            church_founded_date:
              moment(report.church_founded_date).format("YYYY-MM-DD") ||
              prev.churchInfo.church_founded_date,
            report_start_date:
              moment(report.report_start_date).format("YYYY-MM-DD") ||
              prev.churchInfo.report_start_date,
            report_end_date:
              moment(report.report_end_date).format("YYYY-MM-DD") ||
              prev.churchInfo.report_end_date,
            title: report.title || prev.churchInfo.title,
            country_id:
              String(report.country_id || "") || prev.churchInfo.country_id,
            region_id:
              String(report.region_id || "") || prev.churchInfo.region_id,
            zone_id: String(report.zone_id || "") || prev.churchInfo.zone_id,
            city_id: String(report.city_id || "") || prev.churchInfo.city_id,
            sub_city_id:
              String(report.sub_city_id || "") || prev.churchInfo.sub_city_id,
            woreda_id:
              String(report.woreda_id || "") || prev.churchInfo.woreda_id,
            kebele: report.kebele || prev.churchInfo.kebele,
            house_no: report.house_no || prev.churchInfo.house_no,
            specific_location:
              report.specific_location || prev.churchInfo.specific_location,
            contact_person_name:
              report.contact_person_name || prev.churchInfo.contact_person_name,
            contact_person_phone:
              report.contact_person_phone ||
              prev.churchInfo.contact_person_phone,
            contact_person_email:
              report.contact_person_email ||
              prev.churchInfo.contact_person_email,
            latitude: report.latitude || prev.churchInfo.latitude,
            longitude: report.longitude || prev.churchInfo.longitude
          },
          memberCounts: report.member_count || prev.memberCounts,
          resignedMembers: report.member_resigned_count || prev.resignedMembers,
          newMembers: report.new_member_count || prev.newMembers,
          servantsEducation: report.servant_count || prev.servantsEducation,
          churchGrowth: report.new_church_plant_count || prev.churchGrowth,
          pastoralTraining:
            report.servant_training_count || prev.pastoralTraining,
          gospelSpread: report.new_believer_count || prev.gospelSpread,
          homeFellowship:
            report.small_group_participation_count || prev.homeFellowship,
          training: report.training_count || prev.training,
          contributions: report.contributing_count || prev.contributions,
          employees: report.employee_count || prev.employees,
          income: report.income || prev.income,
          properties: report.property_count || prev.properties,
          thanksgiving: report.thanksgiving || prev.thanksgiving,
          migratedBelievers: report.migrated_believers || prev.migratedBelievers
        }));
      }
    }
  }, [reportData]);

  const convertToPositiveNumbers = event => {
    let value = parseFloat(event.target.value);

    // If the value is NaN (invalid number), clear the field
    if (isNaN(value)) {
      event.target.value = "";
      return;
    }

    // If the value is negative, convert to positive
    if (value < 0) {
      event.target.value = Math.abs(value);
    }
  };

  const handleChange = (section, field, value) => {
    setFormState(prev => ({
      ...prev,
      [section]:
        field === "all"
          ? { ...prev[section], ...value } // Handle batched updates
          : typeof field === "object"
          ? merge({}, prev[section], field) // Use lodash merge for deep object updates
          : {
              ...prev[section],
              [field]: value
            }
    }));
  };

  const handleSubmit = event => {
    event.preventDefault();

    denominationReportMutation.mutate(
      {
        formToken: formToken,
        reportData: {
          ...formState.churchInfo,
          member_count: formState.memberCounts,
          member_resigned_count: formState.resignedMembers,
          new_member_count: formState.newMembers,
          servant_count: formState.servantsEducation,
          new_church_plant_count: formState.churchGrowth,
          servant_training_count: formState.pastoralTraining,
          new_believer_count: formState.gospelSpread,
          small_group_participation_count: formState.homeFellowship,
          training_count: formState.training,
          contributing_count: formState.contributions,
          employee_count: formState.employees,
          income: formState.income,
          property_count: formState.properties,
          thanksgiving: formState.thanksgiving,
          migrated_believers: formState.migratedBelievers
        },
        reportId,
        denominationId
      },
      {
        onSuccess: () => {
          setOpenSuccessModal(true);
        }
      }
    );
  };

  const handleSectionComplete = sectionIndex => {
    setCompletedSections(prev => ({
      ...prev,
      [sectionIndex]: true
    }));
    setExpandedSection(sectionIndex + 1);
  };

  const sections = [
    "1. የምዕመናን ብዛት በተመለከት",
    "2. ሪፖርቱ በሚሸፍንበት ጊዜ በልዩ ልዩ ምክንያቶች የተቀነሱ ምዕመናን ብዛት",
    "3. ሪፖርቱ በሚሸፍንበት ጊዜ ከሌሎች ቤተ እምነቶች በሸኚ ወደ ቤተ ክርሰቲያኒቷ የመጡ አማኞች ብዛት",
    "4. ሪፖርቱ በሚሸፍንበት ጊዜ በአጥቢያይቱ ጌታን የተቀበሉ፤ የውሃ ጥምቀት የወሰዱ፤ አሁን ድነት እየተማሩ ያሉ ምዕመናን ብዛት",
    "5. በአጥቢያይቱ ያሉ አገልጋዮች ብዛት እና የት/ት ደረጃቸው",
    "6. ሪፖርቱ በሚሸፍንበት ጊዜ አጥቢያይቱ ሥ/ጣቢያ በመትከልና አጥቢያ በማውጣት ያሳየችው እድገት",
    "7. በአጥቢያይቱ አገልጋዮችን የማብቃት ት/ት በመማር ላይ ያሉ አገልጋዮች ብዛት",
    "8. በአጥቢያይቱ በቤት ሕብረት ምዕመናን ብዛት",
    "9. ሪፖርቱ በሚሸፍንበት ጊዜ የተሰጡ ስልጠናዎች፣ የሰለጠኑ ክፍሎችና የስልጠናው አይነት",
    "10. ሪፖርቱ በሚሸፍንበት ጊዜ አጥቢያይቱ ለዋናው ቢሮና ለክልል ቢሮ የከፈለችው መዋጮ ድምር",
    "11. በአጥቢያይቱ የሚገኙ ቋሚና ጊዜያዊ የአስተዳደር ሰራኞች ብዛት",
    "12. የአጥቢያይቱ ገቢ ሪፖርቱ በሚሸፍንበት ጊዜ",
    "13. የቤተ ክርሰቲያኒቷ ቋሚና ተንቀሳቃሽ ሀብትና ንብረት",
    "14. እግዚአብሔርን የምናመሰግንባቸው ጉዳዮች እና ያጋጠሙ ችግሮች"
  ];

  const isEditingEnabled = useMemo(() => {
    if (!reportId) {
      return true;
    }
    return !!reportId && enableEdit;
  }, [reportId, enableEdit]);

  const sectionComponents = {
    0: (
      <Section0
        formState={formState}
        handleChange={handleChange}
        convertToPositiveNumbers={convertToPositiveNumbers}
        handleSectionComplete={handleSectionComplete}
        reportId={reportId}
        isEditingEnabled={isEditingEnabled}
      />
    ),
    1: (
      <Section1
        formState={formState}
        handleChange={handleChange}
        convertToPositiveNumbers={convertToPositiveNumbers}
        handleSectionComplete={handleSectionComplete}
        reportId={reportId}
        isEditingEnabled={isEditingEnabled}
      />
    ),
    2: (
      <Section2New
        formState={formState}
        handleChange={handleChange}
        convertToPositiveNumbers={convertToPositiveNumbers}
        handleSectionComplete={handleSectionComplete}
        reportId={reportId}
        isEditingEnabled={isEditingEnabled}
      />
    ),
    3: (
      <Section2
        formState={formState}
        handleChange={handleChange}
        convertToPositiveNumbers={convertToPositiveNumbers}
        handleSectionComplete={handleSectionComplete}
        reportId={reportId}
        isEditingEnabled={isEditingEnabled}
      />
    ),
    4: (
      <Section3
        formState={formState}
        handleChange={handleChange}
        handleSectionComplete={handleSectionComplete}
        reportId={reportId}
        isEditingEnabled={isEditingEnabled}
      />
    ),
    5: (
      <Section4
        formState={formState}
        handleChange={handleChange}
        convertToPositiveNumbers={convertToPositiveNumbers}
        handleSectionComplete={handleSectionComplete}
        reportId={reportId}
        isEditingEnabled={isEditingEnabled}
      />
    ),
    6: (
      <Section5
        formState={formState}
        handleChange={handleChange}
        handleSectionComplete={handleSectionComplete}
        reportId={reportId}
        isEditingEnabled={isEditingEnabled}
      />
    ),
    7: (
      <Section6
        formState={formState}
        handleChange={handleChange}
        convertToPositiveNumbers={convertToPositiveNumbers}
        handleSectionComplete={handleSectionComplete}
        reportId={reportId}
        isEditingEnabled={isEditingEnabled}
      />
    ),
    8: (
      <Section7
        formState={formState}
        handleChange={handleChange}
        convertToPositiveNumbers={convertToPositiveNumbers}
        handleSectionComplete={handleSectionComplete}
        reportId={reportId}
        isEditingEnabled={isEditingEnabled}
      />
    ),
    9: (
      <Section8
        formState={formState}
        handleChange={handleChange}
        convertToPositiveNumbers={convertToPositiveNumbers}
        handleSectionComplete={handleSectionComplete}
        reportId={reportId}
        isEditingEnabled={isEditingEnabled}
      />
    ),
    10: (
      <Section9
        formState={formState}
        handleChange={handleChange}
        convertToPositiveNumbers={convertToPositiveNumbers}
        handleSectionComplete={handleSectionComplete}
        reportId={reportId}
        isEditingEnabled={isEditingEnabled}
      />
    ),
    11: (
      <Section10
        formState={formState}
        handleChange={handleChange}
        convertToPositiveNumbers={convertToPositiveNumbers}
        handleSectionComplete={handleSectionComplete}
        reportId={reportId}
        isEditingEnabled={isEditingEnabled}
      />
    ),
    12: (
      <Section11
        formState={formState}
        handleChange={handleChange}
        convertToPositiveNumbers={convertToPositiveNumbers}
        handleSectionComplete={handleSectionComplete}
        reportId={reportId}
        isEditingEnabled={isEditingEnabled}
      />
    ),
    13: (
      <Section12
        formState={formState}
        handleChange={handleChange}
        convertToPositiveNumbers={convertToPositiveNumbers}
        handleSectionComplete={handleSectionComplete}
        reportId={reportId}
        isEditingEnabled={isEditingEnabled}
      />
    )
  };

  if (
    isDenominationLoading ||
    (reportId && isReportsLoading) ||
    countriesQuery.isLoading ||
    denominationChurchesPublicQuery.isLoading
  ) {
    return <LoadingScreen />;
  }

  if (
    isDenominationError ||
    !denomination?.result ||
    !denomination?.result?.form_token ||
    denomination?.result?.form_token !== formToken
  ) {
    return <BrokenLinkScreen />;
  }

  return (
    <Stack sx={{ height: "100vh", overflowY: "auto" }}>
      {/* Header with denomination information */}
      <StyledHeader>
        {reportId && (
          <IconButton
            color="secondary"
            onClick={() => navigate(-1)}
            sx={{
              position: "absolute",
              top: 16,
              left: 24,
              display: { xs: "none", sm: "block" }
            }}
          >
            <ChevronLeft />
          </IconButton>
        )}

        <Avatar
          src={`${IMAGE_URL}/${denomination?.result?.image_url}`}
          alt={denomination?.result?.name}
          sx={{
            border: "2px solid",
            borderColor: "primary.main",
            width: { xs: 70, sm: 100 },
            height: { xs: 70, sm: 100 },
            mb: { xs: 1, sm: 0 }
          }}
        />
        <Stack alignItems="center">
          <Typography variant="h5" align="center" gutterBottom>
            {denomination?.result?.name}
          </Typography>
          <Typography variant="subtitle1" align="center">
            የአገልግሎት ሪፖርት መሙያ ፎርም
          </Typography>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="center"
            spacing={{ xs: 1, sm: 3 }}
            mt={1}
            sx={{ alignItems: { xs: "center", sm: "center" } }}
          >
            <Typography>
              ፖስታ ሳጥን ቁጥር: {denomination?.result?.p_o_box}
            </Typography>
            <Typography>ኢሜል: {denomination?.result?.email}</Typography>
            <Typography>ስልክ: {denomination?.result?.phone}</Typography>
          </Stack>
        </Stack>

        {reportId && (
          <Button
            variant="text"
            onClick={handleClick}
            sx={{
              position: "absolute",
              top: 16,
              right: 24
            }}
          >
            <DotsThreeVerticalIcon color="white" size={24} />
          </Button>
        )}
      </StyledHeader>

      <StyledForm onSubmit={handleSubmit}>
        <Stack spacing={2}>
          {/* Personal Information Section */}
          <Typography variant="subtitle2" fontWeight="bold">
            ስለ አጥቢያው መረጃ
          </Typography>

          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <FormControl fullWidth>
              <Typography variant="caption">የአጥቢያይቱ ስም *</Typography>
              <StyledSelect
                native
                key={formState.churchInfo.denomination_church_id}
                defaultValue={formState.churchInfo.denomination_church_id}
                onBlur={e => {
                  handleChange(
                    "churchInfo",
                    "denomination_church_id",
                    e.target.value
                  );

                  const church = (
                    denominationChurchesPublicQuery.data?.result || []
                  )?.find(
                    church => String(church?.id) === String(e.target.value)
                  );

                  if (church) {
                    handleChange(
                      "churchInfo",
                      "country_id",
                      church?.country_id || ""
                    );
                    handleChange(
                      "churchInfo",
                      "region_id",
                      church?.region_id || ""
                    );
                    handleChange(
                      "churchInfo",
                      "zone_id",
                      church?.zone_id || ""
                    );
                    handleChange(
                      "churchInfo",
                      "city_id",
                      church?.city_id || ""
                    );
                    handleChange(
                      "churchInfo",
                      "sub_city_id",
                      church?.sub_city_id || ""
                    );
                    handleChange(
                      "churchInfo",
                      "woreda_id",
                      church?.woreda_id || ""
                    );
                    handleChange("churchInfo", "kebele", church?.kebele || "");
                    handleChange(
                      "churchInfo",
                      "house_no",
                      church?.house_number || ""
                    );
                    handleChange(
                      "churchInfo",
                      "specific_location",
                      church?.specific_location || ""
                    );
                  }
                }}
                disabled={!isEditingEnabled}
              >
                <option value="" disabled>
                  {t("denomination.reporting.selectChurch")}
                </option>
                {churches.map(church => (
                  <option key={church?.id} value={church?.id}>
                    {church?.name}
                  </option>
                ))}
              </StyledSelect>
            </FormControl>

            <FormControl fullWidth>
              <Typography variant="caption">አጥቢያ የወጣችበት ቀንና ዓ.ም *</Typography>
              <EthiopianDatePicker
                key={formState.churchInfo.church_founded_date}
                value={formState.churchInfo.church_founded_date}
                onChange={e => {
                  handleChange(
                    "churchInfo",
                    "church_founded_date",
                    e.target.value
                  );
                }}
                height={56}
                borderRadius={8}
                disabled={!isEditingEnabled}
              />
            </FormControl>

            <FormControl fullWidth>
              <Typography variant="caption">ሪፖርቱ የሚሸፍነው ጊዜ ከ *</Typography>
              <StyledTextField
                type="date"
                key={formState.churchInfo.report_start_date}
                defaultValue={formState.churchInfo.report_start_date}
                onBlur={e =>
                  handleChange(
                    "churchInfo",
                    "report_start_date",
                    e.target.value
                  )
                }
                disabled={!isEditingEnabled}
              />
            </FormControl>

            <FormControl fullWidth>
              <Typography variant="caption">ሪፖርቱ የሚሸፍነው ጊዜ እስከ *</Typography>
              <StyledTextField
                type="date"
                key={formState.churchInfo.report_end_date}
                defaultValue={formState.churchInfo.report_end_date}
                onBlur={e =>
                  handleChange("churchInfo", "report_end_date", e.target.value)
                }
                disabled={!isEditingEnabled}
              />
            </FormControl>
          </Stack>

          {/* Church Information Section */}
          <Typography variant="subtitle2" fontWeight="bold">
            የአጥቢያው አድራሻ
          </Typography>

          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <FormControl fullWidth>
              <Typography variant="caption">ሀገር *</Typography>
              <StyledSelect
                native
                key={formState.churchInfo.country_id}
                value={formState.churchInfo.country_id}
                onChange={e =>
                  handleChange("churchInfo", "country_id", e.target.value)
                }
                disabled={!isEditingEnabled}
              >
                <option value="" disabled>
                  Select country
                </option>
                {countires.map(country => (
                  <option key={country?.id} value={country?.id}>
                    {country?.name}
                  </option>
                ))}
              </StyledSelect>
            </FormControl>

            <FormControl fullWidth>
              <Typography variant="caption">ክልል *</Typography>
              <StyledSelect
                native
                key={formState.churchInfo.region_id}
                value={formState.churchInfo.region_id}
                onChange={e => {
                  const value = e.target.value;
                  handleChange("churchInfo", "region_id", value);
                  if (
                    value === ADDRESSES.ADDIS_ABABA.REGION_ID ||
                    value === ADDRESSES.DIRE_DAWA.REGION_ID
                  ) {
                    handleChange(
                      "churchInfo",
                      "zone_id",
                      value === ADDRESSES.ADDIS_ABABA.REGION_ID
                        ? ADDRESSES.ADDIS_ABABA.ZONE_ID
                        : ADDRESSES.DIRE_DAWA.ZONE_ID
                    );
                    handleChange(
                      "churchInfo",
                      "city_id",
                      value === ADDRESSES.ADDIS_ABABA.REGION_ID
                        ? ADDRESSES.ADDIS_ABABA.CITY_ID
                        : ADDRESSES.DIRE_DAWA.CITY_ID
                    );
                  } else {
                    handleChange("churchInfo", "zone_id", "");
                    handleChange("churchInfo", "city_id", "");
                  }
                }}
                disabled={!isEditingEnabled}
              >
                <option value="" disabled>
                  Select region
                </option>
                {(regionsQuery.data?.result || []).map(region => (
                  <option key={region?.id} value={region?.id}>
                    {region?.name}
                  </option>
                ))}
              </StyledSelect>
            </FormControl>

            {formState.churchInfo.region_id !==
              ADDRESSES.ADDIS_ABABA.REGION_ID &&
              formState.churchInfo.region_id !==
                ADDRESSES.DIRE_DAWA.REGION_ID && (
                <>
                  <FormControl fullWidth>
                    <Typography variant="caption">ዞን *</Typography>
                    <StyledSelect
                      native
                      key={formState.churchInfo.zone_id}
                      value={formState.churchInfo.zone_id}
                      onChange={e =>
                        handleChange("churchInfo", "zone_id", e.target.value)
                      }
                      disabled={!isEditingEnabled}
                    >
                      <option value="" disabled>
                        Select zone
                      </option>
                      {(zonesQuery.data?.result || []).map(zone => (
                        <option key={zone?.id} value={zone?.id}>
                          {zone?.name}
                        </option>
                      ))}
                    </StyledSelect>
                  </FormControl>
                  <FormControl fullWidth>
                    <Typography variant="caption">ከተማ *</Typography>
                    <StyledSelect
                      native
                      key={formState.churchInfo.city_id}
                      value={formState.churchInfo.city_id}
                      onChange={e =>
                        handleChange("churchInfo", "city_id", e.target.value)
                      }
                      disabled={!isEditingEnabled}
                    >
                      <option value="" disabled>
                        Select city
                      </option>
                      {(citiesQuery.data?.result || []).map(city => (
                        <option key={city?.id} value={city?.id}>
                          {city?.name}
                        </option>
                      ))}
                    </StyledSelect>
                  </FormControl>
                </>
              )}
          </Stack>

          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <FormControl fullWidth>
              <Typography variant="caption">ክፍለ ከተማ *</Typography>
              <StyledSelect
                native
                key={formState.churchInfo.sub_city_id}
                value={formState.churchInfo.sub_city_id}
                onChange={e =>
                  handleChange("churchInfo", "sub_city_id", e.target.value)
                }
                disabled={!isEditingEnabled}
              >
                <option value="" disabled>
                  Select subcity
                </option>
                {(subcitiesQuery.data?.result || []).map(subcity => (
                  <option key={subcity?.id} value={subcity?.id}>
                    {subcity?.name}
                  </option>
                ))}
              </StyledSelect>
            </FormControl>

            <FormControl fullWidth>
              <Typography variant="caption">ወረዳ *</Typography>
              <StyledSelect
                native
                key={formState.churchInfo.woreda_id}
                value={formState.churchInfo.woreda_id}
                onChange={e =>
                  handleChange("churchInfo", "woreda_id", e.target.value)
                }
                disabled={!isEditingEnabled}
              >
                <option value="" disabled>
                  Select woreda
                </option>
                {(woredasQuery.data?.result || []).map(woreda => (
                  <option key={woreda?.id} value={woreda?.id}>
                    {woreda?.name}
                  </option>
                ))}
              </StyledSelect>
            </FormControl>

            {formState.churchInfo.region_id !==
              ADDRESSES.ADDIS_ABABA.REGION_ID &&
              formState.churchInfo.region_id !==
                ADDRESSES.DIRE_DAWA.REGION_ID && (
                <FormControl fullWidth>
                  <Typography variant="caption">ቀበሌ *</Typography>
                  <StyledTextField
                    key={formState.churchInfo.kebele}
                    defaultValue={formState.churchInfo.kebele}
                    onBlur={e =>
                      handleChange("churchInfo", "kebele", e.target.value)
                    }
                    disabled={!isEditingEnabled}
                  />
                </FormControl>
              )}
          </Stack>

          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <FormControl fullWidth>
              <Typography variant="caption">የቤት ቁጥር *</Typography>
              <StyledTextField
                key={formState.churchInfo.house_no}
                defaultValue={formState.churchInfo.house_no}
                onBlur={e =>
                  handleChange("churchInfo", "house_no", e.target.value)
                }
                disabled={!isEditingEnabled}
              />
            </FormControl>

            <FormControl fullWidth>
              <Typography variant="caption">ልዩ ቦታ *</Typography>
              <StyledTextField
                key={formState.churchInfo.specific_location}
                defaultValue={formState.churchInfo.specific_location}
                inputRef={placesRef}
                disabled={!isEditingEnabled}
              />
            </FormControl>
          </Stack>

          <Typography variant="subtitle2" fontWeight="bold">
            ሪፖርቱን ያስገባው ሰው መረጃ
          </Typography>

          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <FormControl fullWidth>
              <Typography variant="caption">ሙሉ ስም *</Typography>
              <StyledTextField
                key={formState.churchInfo.contact_person_name}
                defaultValue={formState.churchInfo.contact_person_name}
                onBlur={e =>
                  handleChange(
                    "churchInfo",
                    "contact_person_name",
                    e.target.value
                  )
                }
                disabled={!isEditingEnabled}
              />
            </FormControl>

            <FormControl fullWidth>
              <Typography variant="caption">ኢሜል *</Typography>
              <StyledTextField
                key={formState.churchInfo.contact_person_email}
                defaultValue={formState.churchInfo.contact_person_email}
                onBlur={e =>
                  handleChange(
                    "churchInfo",
                    "contact_person_email",
                    e.target.value
                  )
                }
                disabled={!isEditingEnabled}
              />
            </FormControl>

            <FormControl fullWidth>
              <Typography variant="caption">ስልክ ቁጥር *</Typography>
              <StyledTextField
                key={formState.churchInfo.contact_person_phone}
                defaultValue={formState.churchInfo.contact_person_phone}
                error={
                  Boolean(formState.churchInfo.contact_person_phone) &&
                  !isValidEthiopianPhoneNumber(
                    formState.churchInfo.contact_person_phone
                  )
                }
                helperText={
                  Boolean(formState.churchInfo.contact_person_phone) &&
                  !isValidEthiopianPhoneNumber(
                    formState.churchInfo.contact_person_phone
                  )
                    ? "እባክዎ ትክክለኛ የኢትዮጵያ ስልክ ቁጥር ያስገቡ (ለምሳሌ፡ +251912345678 ወይም 0912345678)"
                    : " "
                }
                inputProps={{
                  pattern: "^(\\+251|0)\\d{9}$",
                  inputMode: "numeric"
                }}
                onBlur={e => {
                  const value = e.target.value;
                  handleChange("churchInfo", "contact_person_phone", value);
                }}
                disabled={!isEditingEnabled}
              />
            </FormControl>
          </Stack>

          <Stack spacing={1}>
            <Typography fontWeight="bold">
              ከዚህ በታች ያለውን የሪፖርቱን ቅጽ ይሙሉ
            </Typography>
            <Typography variant="body2" color="text.secondary">
              እባክዎን ከዚህ በታች ያለውን የሪፖርት ቅጽ ሲሞሉ በጥንቃቄ እና በ ማስተዋል እንዲሞሉ በጌታ ፍቅር
              እንጠይቃለን።
            </Typography>
          </Stack>

          {/* Statistics Accordion Section */}
          <Stack spacing={1}>
            {sections.map((title, index) => (
              <StyledAccordion
                key={index}
                expanded={reportId ? true : expandedSection === index}
                onChange={() => setExpandedSection(index)}
                elevation={0}
              >
                <AccordionSummary
                  expandIcon={
                    reportId ? null : completedSections[index] ? (
                      <CheckBox color="success" />
                    ) : (
                      <CircleOutlined />
                    )
                  }
                >
                  <StyledAccordionTitle>
                    <Typography
                      fontWeight={expandedSection === index ? "bold" : "normal"}
                    >
                      {title}
                    </Typography>
                  </StyledAccordionTitle>
                </AccordionSummary>
                <AccordionDetails>{sectionComponents[index]}</AccordionDetails>
              </StyledAccordion>
            ))}
          </Stack>

          <MutationError error={denominationReportMutation.error} />

          {/* Submit Buttons */}
          {isEditingEnabled && (
            <Stack direction="row" justifyContent="flex-end" spacing={2}>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                disabled={
                  reportId
                    ? false
                    : Object.keys(completedSections).length !==
                        sections.length || denominationReportMutation.isLoading
                }
              >
                ፎርሙን ላክ
              </Button>
            </Stack>
          )}
        </Stack>
      </StyledForm>

      <SuccessModal
        open={openSuccessModal}
        onClose={() => {
          setOpenSuccessModal(false);
          navigate(-1);
        }}
        title={reportId ? "ሪፖርቱ ተስተካክሏል!" : "ተልኳል!"}
        message={
          reportId
            ? "ሪፖርቱ በተሳካ ሁኔታ ተስተካክሏል"
            : "እንኳን ደስ አሎት ሪፖርቱን በተሳካ ሁኔታ ልከዋል።"
        }
        buttonText="ዝጋ"
      />

      <DeleteModal
        title="Delete Denomination Report"
        name={formState.churchInfo.title || "this report"}
        onDelete={() => {
          denominationReportDeleteMutation.mutate({
            denominationId,
            reportId
          });
        }}
      />

      <Menu
        id="denomination-report-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center"
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right"
        }}
      >
        <MenuItem
          onClick={() => {
            setEnableEdit(true);
            handleClose();
          }}
        >
          <PencilIcon size={16} />{" "}
          <Typography variant="body2" sx={{ ml: 0.5 }}>
            Edit Report
          </Typography>
        </MenuItem>
        <MenuItem
          onClick={() => {
            openDeleteModal();
            handleClose();
          }}
          sx={{ color: theme => theme.palette.error.main }}
        >
          <TrashIcon size={16} />{" "}
          <Typography variant="body2" sx={{ ml: 0.5 }}>
            Delete Report
          </Typography>
        </MenuItem>
      </Menu>
    </Stack>
  );
};

const BrokenLinkScreen = ({ reason }) => {
  const { t } = useTranslation();

  return (
    <Stack alignItems="center" justifyContent="center" height="100vh">
      <Stack px={2} maxWidth={900} spacing={2}>
        <Typography variant="h4" fontWeight={700} textAlign="center">
          {t("common.brokenLink")}
        </Typography>
        <Typography textAlign="center">
          {t("common.brokenLinkDescription")}
        </Typography>
        {reason && <Alert severity="error">{reason}</Alert>}
      </Stack>
    </Stack>
  );
};
